import { useEffect, useRef, useState } from "react";
import {
  restoreBookingDraft,
  rememberBookingDraft,
  forgetBookingDraft,
} from "./booking-draft";
import { Form, Head, Link, router, useForm, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import {
  PageCanvas,
  PageHeader,
  RecordSummary,
} from "@/components/page-canvas";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import CustomerFields, {
  customerTargets,
  type CustomerValues,
} from "@/components/forms/customer-fields";
import VehicleFields, {
  vehicleTargets,
  type VehicleValues,
} from "@/components/forms/vehicle-fields";
import {
  serviceDraft,
  reconcileServiceDrafts,
  type BookingOffering,
} from "./service-draft";
import {
  TotalsSummary,
  type EditorLine,
} from "@/components/commercial/service-editor";
import { FormErrors, errorText } from "@/components/forms/form-errors";
import type { VehicleCatalogue } from "@/pages/vehicles/catalogue-fields";
import type { CustomerMatch } from "@/pages/customers/types";
import {
  locationJobsPath,
  locationCustomersPath,
  locationCustomerReusesPath,
  locationVehiclesPath,
  locationVehicleReusesPath,
  newLocationJobPath,
} from "@/routes";

type Option = { id: string; name: string };
type Customer = Option & {
  revision: string;
  email?: string | null;
  phone?: string | null;
};
type Vehicle = {
  id: string;
  revision?: string;
  make: string;
  model: string;
  registration_number?: string | null;
};
type Offering = BookingOffering;

type Composition = {
  draft_id: string;
  origin: string;
  origin_id?: string;
  request_id?: string;
  [key: string]: string | undefined;
};
type BookingStep = "customer" | "vehicle" | "services" | "schedule" | "review";
const bookingSteps: BookingStep[] = [
  "customer",
  "vehicle",
  "services",
  "schedule",
  "review",
];

type Props = {
  step: BookingStep;
  customer: Customer | null;
  vehicle: Vehicle | null;
  customers: Customer[];
  vehicles: Vehicle[];
  available_vehicles: Vehicle[];
  query: string;
  pagination: { page: number; pages: number };
  enquiry?: { id: string; revision: string } | null;
  offerings: Offering[];
  bays: Option[];
  staff: Option[];
  time_zone: string;
  time_choices: Partial<Record<"starts_at" | "ends_at", string[]>>;
  composition: Composition;
  back_path: string;
  catalogue: VehicleCatalogue;
  sheet: "customer" | "vehicle" | null;
  matches: CustomerMatch[];
  matching_contacts: { email?: string; phone?: string };
  initial: {
    bay_id?: string;
    starts_at?: string;
    ends_at?: string;
    starts_at_offset?: string;
    ends_at_offset?: string;
  };
};

export default function JobForm({
  step,
  customer,
  vehicle,
  customers,
  vehicles,
  available_vehicles,
  query,
  pagination,
  enquiry,
  offerings,
  bays,
  staff,
  time_zone,
  time_choices,
  composition,
  back_path,
  catalogue,
  sheet,
  matches,
  matching_contacts,
  initial,
}: Props) {
  const {
    url,
    props: { auth },
  } = usePage();
  const { t } = useTranslation();
  const [activeSheet, setActiveSheet] = useState(sheet);
  useEffect(() => {
    setActiveSheet(sheet);
  }, [sheet, customer?.id, vehicle?.id]);
  useEffect(() => {
    if (!auth.location) return;
    const address = new URL(url, window.location.origin);
    if (
      address.searchParams.get("composition[draft_id]") === composition.draft_id
    )
      return;
    address.searchParams.set("composition[draft_id]", composition.draft_id);
    router.replace({
      url: `${address.pathname}${address.search}${address.hash}`,
      preserveState: true,
      preserveScroll: true,
    });
  }, [url, auth.location, composition.draft_id]);
  const key = `booking:${auth.account?.id}:${auth.location?.id}:${composition.draft_id}`;
  const completed = useRef(false);
  const progress = useRef<HTMLElement>(null);
  const form = useForm(key, () =>
    restoreBookingDraft(key, {
      job: {
        bay_id: initial.bay_id || "",
        starts_at: initial.starts_at || "",
        ends_at: initial.ends_at || "",
        starts_at_offset: initial.starts_at_offset || "",
        ends_at_offset: initial.ends_at_offset || "",
        assignee_id: "",
        helper_ids: [] as string[],
        services: offerings.map(serviceDraft),
        removed_services: [] as string[],
      },
    }),
  );
  useEffect(() => {
    if (!completed.current) rememberBookingDraft(key, form.data.job);
  }, [key, form.data.job]);
  const quickCustomer = useForm(`${key}:customer`, {
    customer: { name: "", email: "", phone: "", address: "" } as CustomerValues,
  });
  const quickVehicle = useForm(`${key}:vehicle`, {
    vehicle: {
      make: "",
      model: "",
      fabrication_year: "",
      colour: "",
      registration_number: "",
      vin: "",
    } as VehicleValues,
  });
  const { setData } = form;
  useEffect(() => {
    setData((data) => {
      const refreshed = reconcileServiceDrafts(data.job.services, offerings);
      const removed = [
        ...new Set([
          ...(data.job.removed_services || []),
          ...refreshed.removed,
        ]),
      ];
      if (
        JSON.stringify(refreshed.services) ===
          JSON.stringify(data.job.services) &&
        JSON.stringify(removed) ===
          JSON.stringify(data.job.removed_services || [])
      )
        return data;
      return {
        ...data,
        job: {
          ...data.job,
          services: refreshed.services,
          removed_services: removed,
        },
      };
    });
  }, [offerings, setData]);
  const errors = (
    activeSheet === "customer"
      ? quickCustomer.errors
      : activeSheet === "vehicle"
        ? quickVehicle.errors
        : form.errors
  ) as Record<string, unknown>;
  useEffect(() => {
    const navigation = progress.current;
    const active = navigation?.querySelector<HTMLElement>(
      '[aria-current="step"]',
    );
    if (navigation && active)
      navigation.scrollLeft = Math.max(
        0,
        active.offsetLeft - navigation.offsetLeft - 8,
      );
  }, [step, customer?.id, vehicle?.id, form.data.job.services]);
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const current = form.data.job;
  const update = <K extends keyof typeof current>(
    field: K,
    value: (typeof current)[K],
  ) => form.setData("job", { ...current, [field]: value });
  const choose = (values: {
    customer_id?: string;
    vehicle_id?: string;
    q?: string;
    page?: number;
    step?: BookingStep;
  }) =>
    router.get(
      newLocationJobPath(locationId),
      { composition, request_id: enquiry?.id, ...values },
      { preserveState: true, preserveScroll: true },
    );
  const selectedLines = current.services.filter(
    (line) => line.selected === "1",
  );
  const servicesReady =
    selectedLines.length > 0 &&
    !(current.removed_services || []).length &&
    selectedLines.every(
      (line) =>
        !line.review_required &&
        /^\d+$/.test(line.quantity) &&
        Number(line.quantity) >= 1 &&
        Number(line.quantity) <= 10000,
    );
  const scheduleReady =
    !!current.bay_id &&
    !!current.assignee_id &&
    !!current.starts_at &&
    !!current.ends_at &&
    current.ends_at > current.starts_at;
  const activeStep: BookingStep = !customer
    ? "customer"
    : step === "customer" && !enquiry
      ? "customer"
      : !vehicle
        ? "vehicle"
        : ["schedule", "review"].includes(step) && !servicesReady
          ? "services"
          : step === "review" && !scheduleReady
            ? "schedule"
            : step;
  const stepIndex = bookingSteps.indexOf(activeStep);
  const stepPath = (next: BookingStep) =>
    newLocationJobPath(locationId, {
      composition,
      customer_id: customer?.id,
      vehicle_id: vehicle?.id,
      request_id: enquiry?.id,
      step: next,
    });
  const goToStep = (next: BookingStep) =>
    router.visit(stepPath(next), { preserveState: true });
  const nextStep = () => goToStep(bookingSteps[stepIndex + 1]);
  const canContinue =
    activeStep === "customer"
      ? !!customer
      : activeStep === "vehicle"
        ? !!vehicle
        : activeStep === "services"
          ? servicesReady
          : scheduleReady;
  const estimateLines: EditorLine[] = selectedLines.map((line) => ({
    id: line.offering_id,
    name: line.terms.name,
    quantity: line.quantity,
    unit_price: line.terms.price,
    tax_mode: line.terms.tax_mode,
    tax_rate: line.terms.tax_rate,
    currency: line.terms.currency,
    discount_amount: "0",
    discount_percentage: "0",
  }));
  const targets = {
    bay_id: "job-bay_id",
    assignee: "job-assignee_id",
    assignee_id: "job-assignee_id",
    helpers: "job-assignee_id",
    services: "job-services",
    starts_at: "job-starts_at",
    ends_at: "job-ends_at",
    customer: "job-customer-search",
    vehicle: "job-vehicle-heading",
  };
  const inputError = (field: string) =>
    errors[field] ? (
      <p id={`job-${field}-error`} className="text-sm text-destructive">
        {errorText(errors[field])}
      </p>
    ) : null;
  const select = (
    field: "bay_id" | "assignee_id" | "starts_at_offset" | "ends_at_offset",
    label: string,
    options: Option[],
  ) => (
    <div className="space-y-2">
      <Label htmlFor={`job-${field.replace("_offset", "-offset")}`}>
        {label}
      </Label>
      <Select
        name={`job[${field}]`}
        value={current[field]}
        onValueChange={(value) => update(field, value || "")}
        items={Object.fromEntries(
          options.map((option) => [option.id, option.name]),
        )}
        required
      >
        <SelectTrigger
          id={`job-${field.replace("_offset", "-offset")}`}
          className="w-full"
          aria-invalid={!!errors[field]}
          aria-describedby={errors[field] ? `job-${field}-error` : undefined}
        >
          <SelectValue placeholder={t("jobs.choose")} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {inputError(field)}
    </div>
  );
  const closeSheet = (open: boolean) => {
    if (!open) {
      const dirty =
        activeSheet === "customer"
          ? quickCustomer.isDirty
          : quickVehicle.isDirty;
      if (dirty && !window.confirm(t("booking.discard_profile"))) return;
      if (activeSheet === "customer") quickCustomer.resetAndClearErrors();
      if (activeSheet === "vehicle") quickVehicle.resetAndClearErrors();
      setActiveSheet(null);
    }
  };
  return (
    <AppLayout
      showWorkspaceNavigation={false}
      breadcrumbs={[
        {
          title: t("jobs.create"),
          href: newLocationJobPath(locationId, { composition }),
        },
      ]}
    >
      <Head title={t("jobs.create")} />
      <PageCanvas>
        <PageHeader
          title={t("jobs.create")}
          description={t("booking.help")}
          actions={
            <Button
              variant="ghost"
              render={
                <Link
                  href={back_path}
                  onClick={() => forgetBookingDraft(key)}
                />
              }
            >
              {t("customers.cancel")}
            </Button>
          }
        />
        <nav
          ref={progress}
          aria-label={t("staff_ux.booking_steps")}
          className="flex gap-2 overflow-x-auto border-b pb-5"
        >
          {bookingSteps.map((item, index) => {
            const enabled =
              (!enquiry || index > 1) &&
              (index < 1 || !!customer) &&
              (index < 2 || !!vehicle) &&
              (index < 3 || servicesReady) &&
              (index < 4 || scheduleReady);
            const content = (
              <>
                <span
                  aria-hidden="true"
                  className="flex size-6 items-center justify-center rounded-full border text-xs tabular-nums"
                >
                  {index + 1}
                </span>
                {t(`staff_ux.steps.${item}`)}
              </>
            );
            const className = buttonVariants({
              variant: item === activeStep ? "secondary" : "ghost",
              className: "min-h-11 shrink-0 gap-2",
            });
            return enabled ? (
              <Link
                key={item}
                className={className}
                href={stepPath(item)}
                preserveState
                aria-current={item === activeStep ? "step" : undefined}
              >
                {content}
              </Link>
            ) : (
              <span
                key={item}
                aria-disabled="true"
                className={`${className} opacity-50`}
              >
                {content}
              </span>
            );
          })}
        </nav>
        {!activeSheet && <FormErrors errors={errors} targets={targets} />}
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(17rem,22rem)]">
          <div className="min-w-0 space-y-6">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t("staff_ux.step_count", {
                  current: stepIndex + 1,
                  total: bookingSteps.length,
                })}
              </p>
              <h2 className="text-section font-semibold">
                {t(`staff_ux.step_titles.${activeStep}`)}
              </h2>
            </div>
            {activeStep === "customer" && (
              <section
                className="space-y-4 rounded-lg border p-4"
                aria-labelledby="job-customer-heading"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2
                    id="job-customer-heading"
                    className="text-lg font-semibold"
                  >
                    {t("jobs.creation.customer")}
                  </h2>
                  {!enquiry && (
                    <Button
                      variant="outline"
                      onClick={() => setActiveSheet("customer")}
                    >
                      {t("customers.create")}
                    </Button>
                  )}
                </div>
                {customer ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-medium">{customer.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {[customer.email, customer.phone]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    {!enquiry && (
                      <Button variant="outline" onClick={() => choose({})}>
                        {t("jobs.creation.change_customer")}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        const data = new FormData(event.currentTarget);
                        choose({ q: String(data.get("q") || "") });
                      }}
                      className="space-y-2"
                    >
                      <Label htmlFor="job-customer-search">
                        {t("jobs.creation.search_label")}
                      </Label>
                      <div className="flex gap-3">
                        <Input
                          key={query}
                          id="job-customer-search"
                          name="q"
                          type="search"
                          maxLength={200}
                          defaultValue={query}
                          className="min-w-0 flex-1"
                        />
                        <Button type="submit" variant="outline">
                          {t("jobs.creation.search")}
                        </Button>
                      </div>
                      {query && (
                        <Button variant="link" onClick={() => choose({})}>
                          {t("jobs.creation.clear_search")}
                        </Button>
                      )}
                    </form>
                    <ul className="grid gap-3 md:grid-cols-2">
                      {customers.map((choice) => (
                        <li key={choice.id}>
                          <Button
                            variant="outline"
                            className="h-auto min-h-16 w-full justify-start whitespace-normal p-4 text-left"
                            render={
                              <Link
                                href={newLocationJobPath(locationId, {
                                  composition,
                                  customer_id: choice.id,
                                })}
                                preserveState
                                preserveScroll
                              />
                            }
                          >
                            <span className="min-w-0 break-words">
                              <span className="block font-medium">
                                {choice.name}
                              </span>
                              <span className="block text-sm font-normal text-muted-foreground">
                                {[choice.email, choice.phone]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            </span>
                          </Button>
                        </li>
                      ))}
                    </ul>
                    {!customers.length && (
                      <p role="status">
                        {t(
                          query
                            ? "jobs.creation.no_matches"
                            : "jobs.creation.no_customers",
                        )}
                      </p>
                    )}
                  </>
                )}
              </section>
            )}
            {customer && activeStep === "vehicle" && (
              <section
                className="space-y-4 rounded-lg border p-4"
                aria-labelledby="job-vehicle-heading"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2
                    id="job-vehicle-heading"
                    tabIndex={-1}
                    className="text-lg font-semibold"
                  >
                    {t("jobs.creation.choose_vehicle")}
                  </h2>
                  {!enquiry && (
                    <Button
                      variant="outline"
                      onClick={() => setActiveSheet("vehicle")}
                    >
                      {t("vehicles.add")}
                    </Button>
                  )}
                </div>
                {vehicle ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-medium">
                      {vehicle.make} {vehicle.model}
                    </p>
                    {!enquiry && (
                      <Button
                        variant="outline"
                        onClick={() => choose({ customer_id: customer.id })}
                      >
                        {t("jobs.creation.change_selection")}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <ul className="grid gap-3 md:grid-cols-2">
                      {vehicles.map((choice) => (
                        <li key={choice.id}>
                          <Button
                            className="h-auto min-h-16 w-full justify-start whitespace-normal p-4 text-left"
                            variant="outline"
                            render={
                              <Link
                                href={newLocationJobPath(locationId, {
                                  composition,
                                  customer_id: customer.id,
                                  vehicle_id: choice.id,
                                })}
                                preserveState
                                preserveScroll
                              />
                            }
                          >
                            {choice.make} {choice.model}{" "}
                            {choice.registration_number}
                          </Button>
                        </li>
                      ))}
                    </ul>
                    {!vehicles.length && (
                      <p role="status">{t("jobs.creation.no_vehicles")}</p>
                    )}
                    <Link
                      className="text-sm underline"
                      href={`/${locationId}/customers/${customer.id}`}
                    >
                      {t("jobs.creation.manage_vehicles")}
                    </Link>
                  </>
                )}
              </section>
            )}
            {["customer", "vehicle"].includes(activeStep) &&
              !vehicle &&
              pagination.pages > 1 && (
                <nav
                  className="flex items-center gap-3"
                  aria-label={t("customers.pages")}
                >
                  {pagination.page > 1 && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        choose({
                          customer_id: customer?.id,
                          q: query,
                          page: pagination.page - 1,
                        })
                      }
                    >
                      {t("customers.previous")}
                    </Button>
                  )}
                  <span>{t("customers.page", pagination)}</span>
                  {pagination.page < pagination.pages && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        choose({
                          customer_id: customer?.id,
                          q: query,
                          page: pagination.page + 1,
                        })
                      }
                    >
                      {t("customers.next")}
                    </Button>
                  )}
                </nav>
              )}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (activeStep !== "review") {
                  if (canContinue) nextStep();
                  return;
                }
                if (!customer || !vehicle || !servicesReady || !scheduleReady)
                  return;
                form.transform((data) => ({
                  ...data,
                  composition,
                  job: {
                    ...data.job,
                    removed_services: undefined,
                    services: data.job.services.map(
                      ({
                        selected,
                        offering_id,
                        offering_revision,
                        service_revision,
                        quantity,
                      }) => ({
                        selected,
                        offering_id,
                        offering_revision,
                        service_revision,
                        quantity,
                      }),
                    ),
                    customer_id: customer.id,
                    customer_revision: customer.revision,
                    vehicle_id: vehicle.id,
                    vehicle_revision: vehicle.revision,
                    request_id: enquiry?.id,
                    request_revision: enquiry?.revision,
                  },
                }));
                form.post(locationJobsPath(locationId), {
                  preserveScroll: true,
                  onSuccess: () => {
                    completed.current = true;
                    forgetBookingDraft(key);
                    form.resetAndClearErrors();
                  },
                });
              }}
              className="space-y-6"
            >
              {activeStep === "schedule" && (
                <>
                  <fieldset className="space-y-4 rounded-lg border p-4">
                    <legend className="px-2 font-semibold">
                      {t("jobs.interval")}
                    </legend>
                    <p id="job-zone" className="text-sm text-muted-foreground">
                      {t("jobs.zone_help", { zone: time_zone })}
                    </p>
                    {select("bay_id", t("jobs.bay_id"), bays)}
                    <div className="grid gap-4 sm:grid-cols-2">
                      {(["starts_at", "ends_at"] as const).map((field) => (
                        <div key={field} className="space-y-2">
                          <Label htmlFor={`job-${field}`}>
                            {t(`jobs.${field}`)}
                          </Label>
                          <Input
                            id={`job-${field}`}
                            name={`job[${field}]`}
                            type="datetime-local"
                            required
                            value={current[field]}
                            onChange={(event) =>
                              update(field, event.target.value)
                            }
                            aria-invalid={!!errors[field]}
                            aria-describedby={
                              errors[field] ? `job-${field}-error` : "job-zone"
                            }
                          />
                          {inputError(field)}
                          {(time_choices[field]?.length || 0) > 1 &&
                            select(
                              `${field}_offset`,
                              t("jobs.occurrence"),
                              time_choices[field]!.map((offset, index) => ({
                                id: offset,
                                name: t(
                                  index === 0
                                    ? "jobs.first_occurrence"
                                    : "jobs.second_occurrence",
                                  { offset },
                                ),
                              })),
                            )}
                        </div>
                      ))}
                    </div>
                  </fieldset>
                  <fieldset className="space-y-4 rounded-lg border p-4">
                    <legend className="px-2 font-semibold">
                      {t("jobs.team")}
                    </legend>
                    {select("assignee_id", t("jobs.assignee_id"), staff)}
                    <p className="font-medium">{t("jobs.helpers")}</p>
                    <div className="flex flex-wrap gap-4">
                      {staff.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            id={`helper-${member.id}`}
                            name="job[helper_ids][]"
                            value={member.id}
                            checked={current.helper_ids.includes(member.id)}
                            onCheckedChange={(checked) =>
                              update(
                                "helper_ids",
                                checked
                                  ? [...current.helper_ids, member.id]
                                  : current.helper_ids.filter(
                                      (id) => id !== member.id,
                                    ),
                              )
                            }
                          />
                          <Label htmlFor={`helper-${member.id}`}>
                            {member.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {inputError("assignee")}
                    <p className="text-sm text-muted-foreground">
                      {t("jobs.team_help")}
                    </p>
                  </fieldset>
                </>
              )}
              {activeStep === "services" && (
                <fieldset
                  id="job-services"
                  tabIndex={-1}
                  className="space-y-4 rounded-lg border p-4"
                >
                  <legend className="px-2 font-semibold">
                    {t("jobs.services")}
                  </legend>
                  {!offerings.length && <p>{t("jobs.no_services")}</p>}
                  {offerings.map((offering, index) => {
                    const line = current.services.find(
                      (item) => item.offering_id === offering.id,
                    );
                    if (!line) return null;
                    const changeLine = (changes: Partial<typeof line>) =>
                      update(
                        "services",
                        current.services.map((item) =>
                          item.offering_id === offering.id
                            ? { ...item, ...changes }
                            : item,
                        ),
                      );
                    return (
                      <div
                        key={offering.id}
                        className="space-y-3 border-b pb-4 last:border-0"
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`service-${index}`}
                            name={`job[services][${index}][selected]`}
                            value="1"
                            checked={line.selected === "1"}
                            onCheckedChange={(checked) =>
                              changeLine({ selected: checked ? "1" : "0" })
                            }
                          />
                          <Label htmlFor={`service-${index}`}>
                            {offering.name}
                          </Label>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {offering.price} {offering.currency} ·{" "}
                          {t("jobs.minutes", {
                            count: offering.estimated_minutes,
                          })}{" "}
                          · {t(`services.${offering.tax_mode}`)}{" "}
                          {offering.tax_rate}% {offering.tax_label}
                        </p>
                        {line.review_required && (
                          <div
                            role="alert"
                            className="space-y-3 rounded-lg border p-3"
                          >
                            <p>{t("booking.changed_terms")}</p>
                            <p className="text-sm text-muted-foreground">
                              {t("booking.previous_terms")}: {line.terms.name} ·{" "}
                              {line.terms.price} {line.terms.currency} ·{" "}
                              {t(`services.${line.terms.tax_mode}`)}{" "}
                              {line.terms.tax_rate}% {line.terms.tax_label}
                            </p>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() =>
                                changeLine({
                                  ...serviceDraft(offering),
                                  selected: line.selected,
                                  quantity: line.quantity,
                                })
                              }
                            >
                              {t("booking.accept_terms")}
                            </Button>
                          </div>
                        )}
                        <div className="max-w-36 space-y-2">
                          <Label htmlFor={`quantity-${index}`}>
                            {t("jobs.quantity")}
                          </Label>
                          <Input
                            id={`quantity-${index}`}
                            name={`job[services][${index}][quantity]`}
                            type="number"
                            min={1}
                            max={10000}
                            step={1}
                            value={line.quantity}
                            onChange={(event) =>
                              changeLine({ quantity: event.target.value })
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                  {inputError("services")}
                  {(current.removed_services || []).length > 0 && (
                    <div
                      role="alert"
                      className="space-y-3 rounded-lg border p-3"
                    >
                      <p>
                        {t("booking.removed_services", {
                          names: (current.removed_services || []).join(", "),
                        })}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => update("removed_services", [])}
                      >
                        {t("booking.confirm_removal")}
                      </Button>
                    </div>
                  )}
                </fieldset>
              )}
              {activeStep === "review" && (
                <section className="space-y-5 rounded-xl border bg-card p-5">
                  <dl className="grid gap-5 sm:grid-cols-2">
                    {[
                      [t("jobs.creation.customer"), customer?.name],
                      [
                        t("staff_ux.steps.vehicle"),
                        vehicle ? `${vehicle.make} ${vehicle.model}` : "",
                      ],
                      [
                        t("jobs.bay_id"),
                        bays.find((bay) => bay.id === current.bay_id)?.name,
                      ],
                      [
                        t("jobs.assignee_id"),
                        staff.find(
                          (member) => member.id === current.assignee_id,
                        )?.name,
                      ],
                      [
                        t("jobs.starts_at"),
                        current.starts_at.replace("T", " "),
                      ],
                      [t("jobs.ends_at"), current.ends_at.replace("T", " ")],
                      [
                        t("jobs.helpers"),
                        current.helper_ids
                          .map(
                            (id) =>
                              staff.find((member) => member.id === id)?.name,
                          )
                          .filter(Boolean)
                          .join(", ") || "—",
                      ],
                    ].map(([label, value]) => (
                      <div key={label} className="min-w-0">
                        <dt className="text-sm text-muted-foreground">
                          {label}
                        </dt>
                        <dd className="mt-1 break-words font-medium">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <p className="text-sm text-muted-foreground">
                    {t("jobs.zone_help", { zone: time_zone })}
                  </p>
                  <div className="divide-y border-y">
                    {selectedLines.map((line) => (
                      <div
                        key={line.offering_id}
                        className="flex flex-wrap items-start justify-between gap-3 py-4"
                      >
                        <div>
                          <p className="font-medium">{line.terms.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {t(`services.${line.terms.tax_mode}`)}{" "}
                            {line.terms.tax_rate}% {line.terms.tax_label}
                          </p>
                        </div>
                        <p className="tabular-nums">
                          {line.quantity} × {line.terms.price}{" "}
                          {line.terms.currency}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t("staff_ux.review_help")}
                  </p>
                </section>
              )}
              {(!customer || !vehicle) && (
                <p role="status">{t("booking.select_profiles")}</p>
              )}
              {!bays.length && <p role="status">{t("jobs.no_bays")}</p>}
              <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t bg-canvas py-4">
                {stepIndex > (enquiry ? 2 : 0) ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => goToStep(bookingSteps[stepIndex - 1])}
                  >
                    {t("staff_ux.back")}
                  </Button>
                ) : (
                  <span />
                )}
                <Button
                  type="submit"
                  disabled={
                    form.processing ||
                    !canContinue ||
                    (activeStep === "review" &&
                      (!servicesReady || !bays.length || !staff.length))
                  }
                >
                  {form.processing
                    ? t("booking.saving")
                    : activeStep === "review"
                      ? t("jobs.confirm")
                      : t("staff_ux.continue")}
                </Button>
              </div>
            </form>
          </div>
          <aside className="space-y-4">
            <RecordSummary
              title={t("booking.summary")}
              description={
                customer ? customer.name : t("jobs.creation.choose_customer")
              }
            >
              <p>
                {vehicle
                  ? `${vehicle.make} ${vehicle.model}`
                  : t("jobs.creation.choose_vehicle")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("jobs.intake_help")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("booking.draft_help")}
              </p>
            </RecordSummary>
            {selectedLines.length > 0 && (
              <TotalsSummary
                lines={estimateLines}
                amount="0"
                percentage="0"
                help={t("staff_ux.estimate_help")}
              />
            )}
          </aside>
        </div>
        <Sheet open={activeSheet === "customer"} onOpenChange={closeSheet}>
          <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
            <SheetHeader>
              <SheetTitle>{t("customers.create")}</SheetTitle>
              <SheetDescription>{t("booking.quick_customer")}</SheetDescription>
            </SheetHeader>
            <div className="space-y-6 p-4">
              <FormErrors errors={errors} targets={customerTargets} />
              <form
                className="space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  quickCustomer.transform((data) => ({ ...data, composition }));
                  quickCustomer.post(locationCustomersPath(locationId), {
                    preserveScroll: true,
                    onSuccess: () => {
                      setActiveSheet(null);
                      quickCustomer.resetAndClearErrors();
                    },
                  });
                }}
              >
                <CustomerFields
                  values={quickCustomer.data.customer}
                  errors={errors}
                  onChange={(field, value) =>
                    quickCustomer.setData("customer", {
                      ...quickCustomer.data.customer,
                      [field]: value,
                    })
                  }
                />
                <Button type="submit" disabled={quickCustomer.processing}>
                  {t("customers.create")}
                </Button>
              </form>
              {matches.length > 0 && (
                <section className="space-y-4">
                  <h3 className="font-semibold">{t("customers.choose")}</h3>
                  <p>{t("customers.reuse_help")}</p>
                  {matches.map((match) => (
                    <Form
                      key={match.id}
                      action={locationCustomerReusesPath(locationId)}
                      method="post"
                    >
                      <input
                        type="hidden"
                        name="identity_id"
                        value={match.id}
                      />
                      <input
                        type="hidden"
                        name="email"
                        value={matching_contacts?.email || ""}
                      />
                      <input
                        type="hidden"
                        name="phone"
                        value={matching_contacts?.phone || ""}
                      />
                      {Object.entries(composition).map(([field, value]) => (
                        <input
                          key={field}
                          type="hidden"
                          name={`composition[${field}]`}
                          value={value || ""}
                        />
                      ))}
                      <p className="font-medium">{match.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {match.matched_contacts
                          .map((contact) => t(`customers.${contact}`))
                          .join(" · ")}
                      </p>
                      <Button type="submit" variant="outline">
                        {t("customers.use_saved")}
                      </Button>
                    </Form>
                  ))}
                </section>
              )}
            </div>
          </SheetContent>
        </Sheet>
        <Sheet
          open={activeSheet === "vehicle" && !!customer}
          onOpenChange={closeSheet}
        >
          <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
            <SheetHeader>
              <SheetTitle>{t("vehicles.add")}</SheetTitle>
              <SheetDescription>{customer?.name}</SheetDescription>
            </SheetHeader>
            {available_vehicles.length > 0 && (
              <section className="space-y-3">
                <p>{t("vehicles.reuse_help")}</p>
                {available_vehicles.map((choice) => (
                  <Form
                    key={choice.id}
                    action={locationVehicleReusesPath(locationId)}
                    method="post"
                  >
                    <input
                      type="hidden"
                      name="customer_id"
                      value={customer?.id || ""}
                    />
                    <input type="hidden" name="identity_id" value={choice.id} />
                    {Object.entries(composition).map(([field, value]) => (
                      <input
                        key={field}
                        type="hidden"
                        name={`composition[${field}]`}
                        value={value || ""}
                      />
                    ))}
                    <p>
                      {choice.make} {choice.model} {choice.registration_number}
                    </p>
                    <Button type="submit" variant="outline">
                      {t("vehicles.reuse")}
                    </Button>
                  </Form>
                ))}
              </section>
            )}
            <form
              className="space-y-4 p-4"
              onSubmit={(event) => {
                event.preventDefault();
                quickVehicle.transform((data) => ({
                  ...data,
                  composition,
                  customer_id: customer?.id,
                }));
                quickVehicle.post(locationVehiclesPath(locationId), {
                  preserveScroll: true,
                  onSuccess: () => {
                    setActiveSheet(null);
                    quickVehicle.resetAndClearErrors();
                  },
                });
              }}
            >
              <FormErrors errors={errors} targets={vehicleTargets} />
              <VehicleFields
                values={quickVehicle.data.vehicle}
                catalogue={catalogue}
                errors={errors}
                onChange={(field, value) =>
                  quickVehicle.setData("vehicle", {
                    ...quickVehicle.data.vehicle,
                    [field]: value,
                  })
                }
              />
              <Button type="submit" disabled={quickVehicle.processing}>
                {t("vehicles.add")}
              </Button>
            </form>
          </SheetContent>
        </Sheet>
      </PageCanvas>
    </AppLayout>
  );
}
